import SearchIcon from "@mui/icons-material/Search";
import {
  Backdrop,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  TextField,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useController, useFormContext } from "react-hook-form";
import type { OptionRoute, RouteToGivePermisson } from "./PermissionRequest";

const LIST_MAX_HEIGHT = 320;
const ROW_HEIGHT = 48;
const OVERSCAN = 2;

/** MenuItem.Mui-selected と同じ primary 8% */
const SELECTED_ROUTE_ROW_BG = "rgba(50, 145, 196, 0.08)";
const SELECTED_ROUTE_ROW_HOVER_BG = "rgba(50, 145, 196, 0.12)";

interface RouteRowProps {
  route: OptionRoute;
  checked: boolean;
  onToggle: (route: OptionRoute, checked: boolean) => void;
}

const RouteRow = React.memo(function RouteRow({
  route,
  checked,
  onToggle,
}: RouteRowProps) {
  return (
    <Box
      component="label"
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        py: 0,
        px: 1.5,
        ...(checked
          ? {
              bgcolor: SELECTED_ROUTE_ROW_BG,
              "&:focus-within": { bgcolor: SELECTED_ROUTE_ROW_BG },
              "&:hover": { bgcolor: SELECTED_ROUTE_ROW_HOVER_BG },
            }
          : {
              bgcolor: "transparent",
              "&:hover": { bgcolor: theme.palette.action.hover },
            }),
      })}
    >
      <Checkbox
        checked={checked}
        onChange={(_, c) => onToggle(route, c)}
      />
      <ListItemText
        primary={route.title}
        primaryTypographyProps={{
          variant: "body1",
          sx: { textAlign: "left" },
        }}
        sx={{ flex: 1, minWidth: 0, textAlign: "left" }}
      />
    </Box>
  );
});

interface RouteSelectWithFilterProps {
  name: "routes_to_give_permission";
  label: string;
  routes: OptionRoute[];
  routesLoading?: boolean;
  rules?: { required?: boolean };
  error?: boolean;
  helpertext?: React.ReactNode;
}

/**
 * 許可する経路を「経路名で絞り込み + チェックボックスリスト」で選択するコンポーネント。
 * 経路数が多くても絞り込みで表示件数を抑え、パフォーマンスを考慮する。
 */
export const RouteSelectWithFilter: React.FC<RouteSelectWithFilterProps> = (
  props
) => {
  const { name, label, routes, routesLoading = false, rules, error, helpertext } = props;
  const { control, setValue } = useFormContext();
  const [filterText, setFilterText] = useState("");

  const {
    field: { ref, value: selectedRoutes },
  } = useController({
    name,
    control,
    rules: rules ?? {},
    defaultValue: [] as RouteToGivePermisson[],
  });

  // クリック時に即座に表示を更新するためローカル状態を保持（タイムラグ解消）
  const [localSelectedIds, setLocalSelectedIds] = useState<Set<number>>(
    () =>
      new Set(
        (selectedRoutes ?? []).map((r: RouteToGivePermisson) => r.route_id)
      )
  );
  const localSelectedIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const ids = new Set<number>(
      (selectedRoutes ?? []).map((r: RouteToGivePermisson) => r.route_id)
    );
    setLocalSelectedIds(ids);
    localSelectedIdsRef.current = ids;
  }, [selectedRoutes]);

  const filteredRoutes = useMemo(() => {
    const t = filterText.trim().toLowerCase();
    if (!t) return routes;
    return routes.filter((r) => r.title.toLowerCase().includes(t));
  }, [routes, filterText]);

  const [scrollTop, setScrollTopState] = useState(0);
  const scrollTopRef = useRef(0);
  const scrollRafRef = useRef<number | null>(null);

  const setScrollTop = useCallback((value: number) => {
    scrollTopRef.current = value;
    if (scrollRafRef.current !== null) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      setScrollTopState(scrollTopRef.current);
    });
  }, []);

  const visibleRange = useMemo(() => {
    const start = Math.max(
      0,
      Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN
    );
    const visibleCount =
      Math.ceil(LIST_MAX_HEIGHT / ROW_HEIGHT) + OVERSCAN * 2;
    const end = Math.min(filteredRoutes.length, start + visibleCount);
    return { start, end };
  }, [scrollTop, filteredRoutes.length]);

  const visibleRoutes = useMemo(
    () => filteredRoutes.slice(visibleRange.start, visibleRange.end),
    [filteredRoutes, visibleRange.start, visibleRange.end]
  );

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      setScrollTop(e.currentTarget.scrollTop);
    },
    [setScrollTop]
  );

  const handleToggle = useCallback(
    (route: OptionRoute, checked: boolean) => {
      const next = new Set(localSelectedIdsRef.current);
      if (checked) next.add(route.key);
      else next.delete(route.key);
      localSelectedIdsRef.current = next;
      setLocalSelectedIds(next);
      setTimeout(() => {
        const arr: RouteToGivePermisson[] = routes
          .filter((r) => next.has(r.key))
          .map((r) => ({ route_id: r.key, route_name: r.title }));
        setValue(name, arr, { shouldValidate: true });
      }, 0);
    },
    [name, routes, setValue]
  );

  return (
    <>
      <Backdrop
        sx={{ color: "#FFFFFF", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={routesLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <FormControl fullWidth error={error} sx={{ mb: 3 }} variant="outlined">
        <InputLabel
          shrink
          id={`${name}-label`}
          sx={{
            // Checkbox フォーカスで FormControl が focused になりラベルが primary になるのを防ぐ
            "&.Mui-focused:not(.Mui-error)": {
              color: "text.secondary",
            },
          }}
        >
          {label}
        </InputLabel>
        <Box sx={{ pt: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.paper",
            minHeight: 56,
            "&:hover": { borderColor: "text.primary" },
            "&:focus-within": {
              borderColor: "primary.main",
              borderWidth: 2,
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            },
            mb: 1,
          }}
        >
          <SearchIcon
            sx={{ ml: 1.25, color: "action.active", flexShrink: 0 }}
            fontSize="medium"
          />
          <TextField
            inputRef={ref}
            fullWidth
            placeholder="経路名で絞り込み"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            variant="outlined"
            inputProps={{ "aria-label": "経路名で絞り込み" }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { border: "none" },
                "&:hover fieldset": { border: "none" },
                "&.Mui-focused fieldset": { border: "none" },
              },
            }}
          />
        </Box>
        <Box
          aria-invalid={error ? true : undefined}
          sx={{
            maxHeight: LIST_MAX_HEIGHT,
            overflow: "auto",
            borderRadius: 1,
            bgcolor: "background.paper",
            border: 1,
            borderColor: error ? "error.main" : "divider",
          }}
          onScroll={handleScroll}
        >
          {filteredRoutes.length === 0 ? (
            <Box
              sx={{
                p: 2,
                typography: "body1",
                color: "text.secondary",
                textAlign: "left",
              }}
            >
              {routes.length === 0
                ? "経路がありません"
                : "条件に一致する経路はありません"}
            </Box>
          ) : (
            <Box
              sx={{
                height: filteredRoutes.length * ROW_HEIGHT,
                position: "relative",
              }}
            >
              {visibleRoutes.map((route, i) => (
                <Box
                  key={route.key}
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: (visibleRange.start + i) * ROW_HEIGHT,
                    height: ROW_HEIGHT,
                  }}
                >
                  <RouteRow
                    route={route}
                    checked={localSelectedIds.has(route.key)}
                    onToggle={handleToggle}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
      {helpertext && <FormHelperText>{helpertext}</FormHelperText>}
    </FormControl>
    </>
  );
};

export default RouteSelectWithFilter;
